// Вкладка «Поездки»: все поездки сервиса одним списком.
//
// Список пользователей отвечает на вопрос «кто у нас есть», этот — на вопрос
// «что у нас лежит». Поездок больше, чем людей (у одного их сколько угодно), и
// разговор с поддержкой обычно начинается именно с поездки: «не сохраняется»,
// «пропал вариант», «поделился, а там пусто».
//
// Отсюда же открывается правка: кнопка «Войти как владелец» пускает
// администратора в чужой аккаунт с этой поездкой уже выбранной — чинить руками
// то, что не чинится советом в переписке.
import { useCallback, useEffect, useState, type ReactElement } from 'react';
import { useI18n, useT } from '../i18n';
import { errorKey, impersonate } from '../auth';
import type { ApiError } from '../auth';
import { ConfirmDialog } from '../ui/forms';
import { CURRENT_TRIP_KEY } from '../trips';
import { listAllTrips } from './api';
import type { AdminStats, AdminTrip } from './api';
import { TripStateModal } from './TripStateModal';
import styles from './AdminPage.module.css';

const PAGE_SIZE = 25;

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export interface TripsPanelProps {
  /** Сводка приходит сверху: её уже загрузила страница для вкладки людей. */
  stats: AdminStats | null;
}

export function TripsPanel({ stats }: TripsPanelProps): ReactElement {
  const t = useT();
  const { lang } = useI18n();

  const [query, setQuery] = useState('');
  const [search, setSearch] = useState('');
  const [offset, setOffset] = useState(0);
  const [trips, setTrips] = useState<AdminTrip[]>([]);
  const [total, setTotal] = useState(0);
  const [error, setError] = useState<ApiError | null>(null);
  const [viewing, setViewing] = useState<AdminTrip | null>(null);
  // Вход в чужой аккаунт спрашивают отдельно: действие видно владельцу (в его
  // поездке появятся правки) и попадает в журнал.
  const [entering, setEntering] = useState<AdminTrip | null>(null);

  const formatDate = useCallback(
    (ms: number | null) =>
      ms ? new Intl.DateTimeFormat(lang, { dateStyle: 'short', timeStyle: 'short' }).format(ms) : '—',
    [lang],
  );

  const reload = useCallback(async () => {
    const res = await listAllTrips({ q: search, limit: PAGE_SIZE, offset });
    if (res.ok) {
      setTrips(res.value.trips);
      setTotal(res.value.total);
      setError(null);
    } else {
      setError(res.error);
    }
  }, [search, offset]);

  useEffect(() => {
    void reload();
  }, [reload]);

  // Поиск с задержкой: список перезапрашивается не на каждую букву.
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearch(query.trim());
      setOffset(0);
    }, 250);
    return () => clearTimeout(timer);
  }, [query]);

  /**
   * Войти в аккаунт владельца с этой поездкой наготове. id поездки кладём в то
   * же хранилище, откуда его читает список поездок: после перезагрузки
   * планировщик откроется ровно на ней, а не на самой свежей у владельца.
   *
   * Перезагрузка страницы, а не переход роутером: вместе с сессией меняется всё
   * — пользователь, список поездок, состояние планировщика.
   */
  async function enter(trip: AdminTrip): Promise<void> {
    setEntering(null);
    if (!trip.ownerId) return;
    const res = await impersonate(trip.ownerId);
    if (!res.ok) {
      setError(res.error);
      return;
    }
    try {
      window.localStorage.setItem(CURRENT_TRIP_KEY, trip.id);
    } catch {
      // Хранилище недоступно — откроется поездка по умолчанию, и только.
    }
    window.location.assign('/');
  }

  const to = Math.min(offset + PAGE_SIZE, total);

  return (
    <div className={styles.usersPanel}>
      {stats && (
        <div className={styles.stats}>
          <div className={styles.stat}>
            <span className={styles.statValue}>{stats.workspaces}</span>
            <span className={styles.statLabel}>{t('admin.stats.trips')}</span>
          </div>
          <div className={styles.stat}>
            <span className={styles.statValue}>{stats.tripsShared}</span>
            <span className={styles.statLabel}>{t('admin.stats.tripsShared')}</span>
          </div>
          <div className={styles.stat}>
            <span className={styles.statValue}>{stats.tripsPublished}</span>
            <span className={styles.statLabel}>{t('admin.stats.tripsPublished')}</span>
          </div>
          <div className={styles.stat}>
            <span className={styles.statValue}>{stats.snapshots}</span>
            <span className={styles.statLabel}>{t('admin.stats.snapshots')}</span>
          </div>
          <div className={styles.stat}>
            <span className={styles.statValue}>{formatBytes(stats.stateBytes)}</span>
            <span className={styles.statLabel}>{t('admin.stats.size')}</span>
          </div>
        </div>
      )}

      <div className={styles.toolbar}>
        <input
          className="input"
          type="search"
          value={query}
          placeholder={t('admin.trips.search')}
          aria-label={t('admin.trips.search')}
          onChange={(e) => setQuery(e.target.value)}
        />
      </div>

      {error && (
        <p className={styles.error} role="alert">
          {t(errorKey(error))}
        </p>
      )}

      <div className={styles.tableWrap}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th>{t('admin.trips.col.trip')}</th>
              <th>{t('admin.trips.col.owner')}</th>
              <th>{t('admin.trips.col.members')}</th>
              <th>{t('admin.trips.col.share')}</th>
              <th>{t('admin.trips.col.created')}</th>
              <th>{t('admin.trips.col.updated')}</th>
              <th>{t('admin.col.data')}</th>
              <th aria-label={t('admin.col.actions')} />
            </tr>
          </thead>
          <tbody>
            {trips.map((trip) => (
              <tr key={trip.id} className={trip.ownerDeleted ? styles.deletedRow : undefined}>
                <td>
                  <span className={styles.email}>{trip.name.trim() || t('trips.untitled')}</span>
                  <span className={`${styles.name} mono`}>{trip.id}</span>
                </td>
                <td>
                  <span className={styles.email}>{trip.ownerEmail || '—'}</span>
                  {trip.ownerName && <span className={styles.name}>{trip.ownerName}</span>}
                  {trip.ownerDeleted && (
                    <span className={styles.badgeWarn}>{t('admin.status.deleted')}</span>
                  )}
                </td>
                <td className="mono">{trip.members}</td>
                <td>
                  {trip.shareId ? (
                    <a
                      className={styles.email}
                      href={`/s/${encodeURIComponent(trip.shareId)}`}
                      target="_blank"
                      rel="noreferrer"
                    >
                      {t('admin.trips.published')}
                    </a>
                  ) : (
                    '—'
                  )}
                </td>
                <td className="mono">{formatDate(trip.createdAt)}</td>
                <td className="mono">{formatDate(trip.updatedAt)}</td>
                <td className="mono">{formatBytes(trip.size)}</td>
                <td>
                  <div className={styles.actions}>
                    <button type="button" className="btn btn-sm" onClick={() => setViewing(trip)}>
                      {t('admin.action.view')}
                    </button>
                    {/* Войти можно только к живому владельцу: у удалённого
                        аккаунта нет действующей сессии, и подделать её нельзя. */}
                    <button
                      type="button"
                      className="btn btn-sm"
                      disabled={!trip.ownerId || trip.ownerDeleted}
                      onClick={() => setEntering(trip)}
                    >
                      {t('admin.trips.enter')}
                    </button>
                  </div>
                </td>
              </tr>
            ))}
            {trips.length === 0 && (
              <tr>
                <td colSpan={8} className={styles.empty}>
                  {t('admin.trips.empty')}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {total > PAGE_SIZE && (
        <div className={styles.pager}>
          <button
            type="button"
            className="btn btn-sm"
            disabled={offset === 0}
            onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
          >
            {t('admin.prev')}
          </button>
          <span className={styles.pageInfo}>
            {t('admin.page', { from: total === 0 ? 0 : offset + 1, to, total })}
          </span>
          <button
            type="button"
            className="btn btn-sm"
            disabled={to >= total}
            onClick={() => setOffset(offset + PAGE_SIZE)}
          >
            {t('admin.next')}
          </button>
        </div>
      )}

      {viewing && <TripStateModal trip={viewing} onClose={() => setViewing(null)} />}

      <ConfirmDialog
        open={entering !== null}
        title={t('admin.trips.enter')}
        message={t('impersonate.confirm', { email: entering?.ownerEmail ?? '' })}
        confirmLabel={t('admin.trips.enter')}
        onConfirm={() => {
          if (entering) void enter(entering);
        }}
        onCancel={() => setEntering(null)}
      />
    </div>
  );
}
